import { useState, useEffect, useRef } from "react";
import { assetUrl, api, downloadUrl, exportUrl } from "@/lib/api";
import { DownloadButton, IconDownload } from "@/components/DownloadButton";
import { Button } from "@/components/ui/button";
import { Volume2, Square, ChevronLeft, ChevronRight, Loader2, ImageOff, FileDown, Package } from "lucide-react";
import { toast } from "sonner";
import { exportStorybookPdf } from "@/lib/exportStorybook";

export const StorybookReader = ({ project }) => {
  const book = project.storybook;
  const [page, setPage] = useState(0);
  const [speaking, setSpeaking] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [localAudio, setLocalAudio] = useState({});
  const audioRef = useRef(null);

  useEffect(() => () => audioRef.current?.pause(), []);
  useEffect(() => { audioRef.current?.pause(); setSpeaking(false); }, [page]);

  if (!book)
    return project.status === "processing" ? (
      <div className="glass rounded-2xl py-20 text-center text-[#94A3B8]"><Loader2 className="h-8 w-8 mx-auto text-[#A855F7] animate-spin mb-3" />Storybook is being generated… pages will appear here shortly.</div>
    ) : project.status === "error" ? (
      <div className="glass rounded-2xl py-20 text-center text-red-300">Storybook failed to generate. See the developer drawer logs.</div>
    ) : (
      <div className="glass rounded-2xl py-20 text-center text-[#94A3B8]">Storybook is not available for this project.</div>
    );

  const pages = book.pages || [];
  const p = pages[page];

  const exportPdf = async () => {
    setExporting(true);
    try { await exportStorybookPdf(book, project.output_language); toast.success("Storybook PDF downloaded"); }
    catch { toast.error("PDF export failed"); }
    setExporting(false);
  };

  const speak = async () => {
    try {
      setSpeaking("loading");
      const aid = p.audio_asset_id || localAudio[page] || (await api.pageNarration(project.id, page)).audio_asset_id;
      setLocalAudio((m) => ({ ...m, [page]: aid }));
      audioRef.current?.pause();
      const a = new Audio(assetUrl(aid));
      audioRef.current = a;
      a.onended = () => setSpeaking(false);
      await a.play();
      setSpeaking(true);
    } catch {
      setSpeaking(false);
      toast.error("Couldn't play narration. Check your volume and try again.");
    }
  };
  const stop = () => { audioRef.current?.pause(); setSpeaking(false); };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="font-display text-2xl font-semibold">{book.title}</h2>
        {book.moral && <p className="text-[#94A3B8] mt-1 italic">{book.moral}</p>}
        <div className="flex flex-wrap justify-center gap-2 mt-4">
        <Button
          data-testid="export-storybook-pdf-btn"
          onClick={exportPdf}
          disabled={exporting || project.status === "processing"}
          variant="outline"
          className="rounded-full border-[#A855F7]/40 bg-[#A855F7]/10 text-[#E9D5FF] hover:bg-[#A855F7]/20"
        >
          {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
          {exporting ? "Building PDF…" : "Download as PDF"}
        </Button>
        <DownloadButton testid="download-storybook-pack-btn" href={exportUrl(project.id, "storybook-pack")} filename="storybook-pack.zip"
          label="Pictures + narration (.zip)" icon={Package} disabled={project.status === "processing"} />
        </div>
      </div>

      <div className="glass rounded-2xl overflow-hidden" data-testid="storybook-page">
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Illustration */}
          <div className="relative aspect-square md:aspect-auto md:min-h-[360px] bg-[#0F1117] flex items-center justify-center">
            {p?.image_asset_id ? (
              <img data-testid="storybook-img" src={assetUrl(p.image_asset_id)} alt="" className="h-full w-full object-cover" />
            ) : project.status === "processing" ? (
              <div className="flex flex-col items-center gap-2 text-[#94A3B8]"><Loader2 className="h-6 w-6 animate-spin text-[#A855F7]" /><span className="text-xs">Illustrating…</span></div>
            ) : (
              <ImageOff className="h-10 w-10 text-[#334155]" />
            )}
            <div className="absolute top-3 left-3 bg-black/60 text-white text-xs px-2.5 py-1 rounded-full">Page {p?.number} / {pages.length}</div>
            {p?.image_asset_id && (
              <div className="absolute top-3 right-3"><IconDownload testid="download-illustration-btn" title="Download illustration" href={downloadUrl(p.image_asset_id, `page-${p.number}-illustration`)} /></div>
            )}
          </div>

          {/* Bilingual text */}
          <div className="p-6 sm:p-8 flex flex-col">
            <div className="flex-1 space-y-5">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#22C55E] mb-1.5">{project.output_language}</div>
                <p data-testid="native-text" className="font-serif-i text-xl leading-relaxed text-[#F8FAFC]">{p?.native_text}</p>
              </div>
              {p?.phonetic && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#A855F7] mb-1">Pronunciation</div>
                  <p className="text-sm text-[#94A3B8] italic">{p.phonetic}</p>
                </div>
              )}
              <div className="pt-4 border-t border-white/10">
                <div className="text-[10px] uppercase tracking-widest text-[#3B82F6] mb-1.5">English</div>
                <p data-testid="english-text" className="text-base leading-relaxed text-[#CBD5E1]">{p?.english_text}</p>
              </div>
            </div>

            <Button
              data-testid="tts-narrate-btn"
              onClick={speaking ? stop : speak}
              disabled={speaking === "loading"}
              className={`mt-6 rounded-full ${speaking === true ? "bg-red-500 hover:bg-red-600" : "bg-[#A855F7] hover:bg-[#9333EA]"} text-white`}
            >
              {speaking === "loading" ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Preparing narration…</>
                : speaking ? <><Square className="h-4 w-4 mr-2" /> Stop Narration</> : <><Volume2 className="h-4 w-4 mr-2" /> Read Aloud</>}
            </Button>
          </div>
        </div>
      </div>

      {/* Page nav */}
      <div className="flex items-center justify-center gap-4">
        <Button data-testid="prev-page-btn" variant="outline" disabled={page === 0} onClick={() => setPage((x) => x - 1)} className="rounded-full border-white/10 bg-white/5 hover:bg-white/10 disabled:opacity-40">
          <ChevronLeft className="h-4 w-4 mr-1" /> Prev
        </Button>
        <div className="flex gap-1.5">
          {pages.map((_, i) => (
            <button key={i} onClick={() => setPage(i)} className={`h-2 rounded-full transition-all ${i === page ? "w-6 bg-[#A855F7]" : "w-2 bg-white/20"}`} />
          ))}
        </div>
        <Button data-testid="next-page-btn" variant="outline" disabled={page === pages.length - 1} onClick={() => setPage((x) => x + 1)} className="rounded-full border-white/10 bg-white/5 hover:bg-white/10 disabled:opacity-40">
          Next <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};
